<template>
  <v-container>
    <v-dialog
      v-model="geotagDestinationDialog.isOpened"
      max-width="400px"
    >
      <v-card>
        <v-card-title>
          {{ `${geotagDestinationDialog.geotagName}(${geotagDestinationDialog.geotagId})` }}
          route map
        </v-card-title>
        <v-card-text>
          <v-simple-table>
            <tbody>
            <tr>
              <td>Placenames ID</td>
              <td>{{ geotagDestinationDialog.placenamesIds.join(', ') }}</td>
            </tr>
            <tr>
              <td>Name</td>
              <td>{{ geotagDestinationDialog.routeName.join(', ')}}</td>
            </tr>
            <tr>
              <td>IATA/ICAO</td>
              <td>{{ geotagDestinationDialog.iataIcao.join(', ') }}</td>
            </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="closeGeotagDestinationDialog"
          >
            Ok
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-row
      v-show="customer.id"
      dense align="center"
      justify="center"
    >
      <v-col>
        <span class="font-weight-bold">{{ customer.name }}</span>
        {{ ` ${customer.full_name} (ID: ${customer.id})` }}
        <span v-if="customer.airline">(airline: {{ customer.airline }})</span>
      </v-col>
      <v-spacer></v-spacer>
      <XlsxExport
        v-if="!hasSomeAbilities($constants.ABILITY.INTERN)"
        :filename="getExportSummaryWorkbook().filename"
        :sheets="getExportSummaryWorkbook().sheets"
        tooltip="export DB relations"
      />
      <v-btn
        v-if="!hasSomeAbilities($constants.ABILITY.INTERN)"
        @click="exportBBoxes"
        class="mx-2"
        color="primary"
        dark
        fab
        small
      >
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <slot>
              <v-icon
                v-bind="attrs"
                v-on="on"
                dark
              >
                mdi-export
              </v-icon>
            </slot>
          </template>
          export bbox
        </v-tooltip>
      </v-btn>

    </v-row>

    <v-row
      class="mb-3">
      <v-col>
        <v-tabs
          v-model="currentCustomerTab"
        >
          <v-tab
            v-for="(tab, key) in customerTabs"
            :key="key"
          >
            {{ tab }}
          </v-tab>
        </v-tabs>
      </v-col>
    </v-row>

    <v-tabs-items
      v-model="currentCustomerTab"
    >
      <v-tab-item
        :key="0"
      >
        <v-row
          v-if="customerHeadBars.length"
        >
          <v-col
            v-for="(bar, index) in customerHeadBars"
            :cols="Math.floor(12/customerHeadBars.length)"
            :key="index"
          >
            <GreyBarWithTitleAndValue
            :title = bar.title
            :value = bar.value
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col
            :cols="6"
          >
            <GoogleMapMarkers
              :items="geotagsOnMap"
              :isVisible="true"
            />
          </v-col>
          <v-col
            :cols="6"
          >
            Priority Destination Coverage
            <CustomerRouteExpandableTable
              :routes="customerRoutesWithGeotagsWithoutRoutes"
              :route-map-categories="routeMapCategories"
              :geotags-without-routes="geotagsWithoutRoutes"
              :customer-name="customer.name"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col
            :cols="4"
          >
            <SimpleList
              label="Reviews"
              :emphasized-text="customerContent.reviewsTotalAmount"
              :main-list="customerContent.providerRating"
            />
          </v-col>
          <v-col
            :cols="4"
          >
            <SimpleList
              label="Hashtags"
              :emphasized-text="customerContent.poiHashtagsTotalAmount"
              :main-list="customerContent.hashtagRating"
            />
          </v-col>
          <v-col
            :cols="4"
          >
            <SimpleList
              label="Guides"
              :emphasized-text="customerContent.geotagHashtagsTotalAmount"
              :main-list="customerContent.hashtagGeotagRating"
            />
          </v-col>
        </v-row>
      </v-tab-item>
      <v-tab-item
        :key="1"
      >
        <v-row>
          <v-col
            cols="6"
          >
            <v-btn
              :disabled="!!geotagsTotal.length"
              outlined
              @click="extractGeotagsFromCustomerRoutes"
            >
              Add geotags from RM
            </v-btn>
            <br />
            <br />
            <v-btn
              outlined
              @click="updateWeightByRoutemap()"
            >
              Update geotags weight from RM
            </v-btn>
            <br />
            <br />
            <confirm-dialog
                :is-open.sync="showSelectColumns"
                dialog-max-width="800"
                @confirm="saveSelectedColumns"
                confirm-button-text="Update"
            >
              <template v-slot:activator>
                <v-btn
                    color="blue darken-1"
                    outlined
                    @click="showSelectColumns = true"
                >
                  Select columns
                </v-btn>
              </template>
              <template v-slot:title>
                Select columns
              </template>
              <template v-slot:body>
                <v-row>
                  <v-col
                      v-for="header in geotagsTableHeaders"
                      :key="header.value"
                      cols="3"
                  >
                    <v-checkbox
                        v-model="selectedColumns"
                        :label="header.text ? header.text : header.value"
                        :value="header.value"
                        :disabled="header.isRequired"
                    ></v-checkbox>
                  </v-col>
                </v-row>
              </template>
            </confirm-dialog>
          </v-col>
          <v-col
            cols="6"
          >
            <Chart
              :chart-data="geotagCharts"
              :options="chartOptions"
              :width="500"
              :height="150"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-data-table
              :options.sync="table.options"
              :footer-props="{ itemsPerPageOptions: [25, 50, 100, 200 -1] }"
              :headers="computedGeotagsHeaders"
              :items="geotagsTotal"
              :loading="standby"
              loading-text="Loading... Please wait"
              :single-expand="true"
            >
              <template v-slot:body.prepend>
                <tr class="filter-row">
                  <td v-for="(header, key) in computedGeotagsHeaders" :key="key">
                    <!-- select -->
                    <v-autocomplete
                      v-if="header.filterOptions && header.filterOptions.type === 'select'"
                      v-model="geotagFilters[header.value]"
                      :items="header.filterOptions.items"
                      :item-text="header.filterOptions.itemText ? header.filterOptions.itemText : 'name'"
                      :item-value="header.filterOptions.itemValue ? header.filterOptions.itemValue : 'id'"
                      :label="header.text"
                      :filter="nonDiacriticsSensitiveFilter"
                      clearable
                      dense
                    />
                    <!-- multiple select -->
                    <v-autocomplete
                      v-else-if="header.filterOptions && header.filterOptions.type === 'multipleSelect'"
                      v-model="geotagFilters[header.value]"
                      :items="header.filterOptions.items"
                      :item-text="header.filterOptions.itemText ? header.filterOptions.itemText : 'name'"
                      :item-value="header.filterOptions.itemValue ? header.filterOptions.itemValue : 'id'"
                      :label="header.text"
                      :filter="nonDiacriticsSensitiveFilter"
                      clearable
                      multiple
                      dense
                      chips
                      small-chips
                      deletable-chips
                      :menu-props="{ closeOnContentClick: true }"
                    />
                    <!-- default -->
                    <v-text-field v-else
                        v-model="geotagFilters[header.value]"
                        type="text"
                        :label="header.text"
                        clearable
                        dense
                    />
                  </td>
                </tr>
              </template>

              <template v-slot:item.id="{ item }">
                {{ item.id }}
                <v-btn
                  v-if="isGeotagRoutemapDestination(item.id)"
                  class="ml-10"
                  icon
                  @click="openGeotagDestinationDialog(item.id, item.name)"
                >
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <route-svg
                        v-bind="attrs"
                        v-on="on"
                      />
                    </template>
                    show route map
                  </v-tooltip>
                </v-btn>
              </template>

              <template v-slot:item.name="{ item }">
                <span>{{ item.name }}</span>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-bind="attrs"
                      v-on="on"
                      :to="`../geotag/${item.id}`" target="_blank"
                      icon
                      small
                    >
                      <v-icon color="orange" dense>
                        mdi-pen
                      </v-icon>
                    </v-btn>
                  </template>
                  geotag page
                </v-tooltip>
              </template>

              <template v-slot:item.pois_count="{ item }">
                <div><span class="font-italic">total</span>: {{ item.pois_count }}</div>
                <span class="font-italic">enabled</span>:
                <span class=" green--text">{{ item.pois_enabled_count }}</span>
              </template>

              <template v-slot:item.pois_with_video_count="{ item }">
                <div><span class="font-italic">total</span>: {{ item.pois_with_video_count }}</div>
                <span class="font-italic">enabled</span>:
                <span class=" green--text">{{ item.pois_enabled_with_video_count }}</span>
              </template>

              <template v-slot:item.pois_with_images_count="{ item }">
                <div><span class="font-italic">total</span>: {{ item.pois_with_images_count }}</div>
                <span class="font-italic">enabled</span>:
                <span class=" green--text">{{ item.pois_enabled_with_images_count }}</span>
              </template>

              <template v-slot:item.status="{ item }">
                <TripleButton
                  :status="item.status"
                  :disabled="true"
                />
              </template>

              <template v-slot:item.native_status="{ item }">
                <v-switch
                  :input-value="item.native_status"
                  disabled
                />
              </template>

              <template v-slot:item.without_attractions_for_native="{ item }">
                <v-switch
                  v-model="item.customers[0].without_attractions_for_native"
                  @change="save({...item.customers[0]}, item)"
                >
                  <template v-slot:prepend>

                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs}">
                        <v-icon
                            v-bind="attrs"
                            v-on="on"
                            x-small
                            style="margin-top: 7px;"
                            :color="item.geotag_images_count ? 'green' : 'gray'">
                          mdi-circle
                        </v-icon>
                      </template>
                      <span v-if="item.geotag_images_count">Geotag image available</span>
                      <span v-else>No geotag image</span>
                    </v-tooltip>
                  </template>
                </v-switch>
              </template>

              <template v-slot:item.is_destination="{ item }">
                <v-switch
                  v-if="!customer.is_ground"
                  v-model="item.customers[0].is_destination"
                  @change="save({...item.customers[0]}, item)"
                />
                <v-tooltip
                  v-else
                  bottom
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-switch
                      v-bind="attrs"
                      v-on="on"
                      :value="false"
                      disabled
                    />
                  </template>
                  not available for ground
                </v-tooltip>
              </template>

              <template v-slot:item.customers.0.weight="{ item }">
                <v-row align="center" justify="center">
                  <v-edit-dialog
                    @save="save(itemClone, item)"
                    @open="itemClone = {...item.customers[0]}"
                  >
                    {{ item.customers[0].weight }}
                    <template v-slot:input>
                      <v-text-field
                        v-model="itemClone.weight"
                        label="Edit"
                        single-line
                        counter
                      />
                    </template>
                  </v-edit-dialog>

                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs}">
                      <v-btn
                        @click="updateWeightByRoutemap(item)"
                        icon
                        small
                        class="ml-4"
                        v-bind="attrs"
                        v-on="on"
                      >
                        <v-icon color="orange" dense>
                          mdi-reload
                        </v-icon>
                      </v-btn>
                    </template>
                    update from RM
                  </v-tooltip>
                </v-row>
              </template>

              <template v-slot:item.customers.0.subtype_id="{ item }">
                <v-edit-dialog
                    @save="save(itemClone, item)"
                    @open="itemClone = {...item.customers[0]}"
                >
                  <div v-if="item.customers[0].subtype_id">
                    {{ getSubtypeName(item.customers[0].subtype_id) }}
                  </div>
                  <div
                      v-else
                      class="font-italic font-weight-thin red--text" v-text="'empty'"
                  />
                  <template v-slot:input>
                    <v-select
                        v-model="itemClone.subtype_id"
                        :items="geotagFilters.subtypeItems"
                        item-text="name"
                        item-value="id"
                        clearable
                    ></v-select>
                  </template>
                </v-edit-dialog>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-tab-item>
      <v-tab-item
        :key="2"
      >
        <v-row>
          <v-col>
            <v-data-table
              ref="summaryroute"
              :options.sync="mainRouteMapTable.options"
              :footer-props="{ itemsPerPageOptions: [25, 50, 100, 200 -1] }"
              :headers="mainRouteMapTableHeaders"
              :items="customerRoutesWithGeotagsWithoutRoutes"
              :loading="standby"
              loading-text="Loading... Please wait"
            >
              <template v-slot:body.prepend>
                <tr class="filter-row">
                  <td>
                    <v-autocomplete
                      ref="routeselect"
                      v-model="mainRouteMapTable.filters.routeNames"
                      :items="routeNamesList"
                      :filter="nonDiacriticsSensitiveFilter"
                      label="routes destinations"
                      clearable
                      multiple
                      dense
                      chips
                      small-chips
                      deletable-chips
                      :menu-props="{
                          closeOnContentClick: true,
                        }"
                    />
                  </td>
                  <td>
                    <v-text-field
                      :value="mainRouteMapTable.filters.placenameIds"
                      @input="v=>mainRouteMapTable.filters.placenameIds=v"
                      type="text"
                      label="placenames IDs"
                      clearable
                      dense
                    />
                  </td>
                  <td>
                    <v-autocomplete
                      ref="geotagselect"
                      v-model="mainRouteMapTable.filters.geotagNames"
                      :items="geotagNamesList"
                      :filter="nonDiacriticsSensitiveFilter"
                      label="geotags"
                      clearable
                      multiple
                      dense
                      chips
                      small-chips
                      deletable-chips
                      :menu-props="{
                          closeOnContentClick: true,
                        }"
                    />
                  </td>
                  <td>
                    <v-text-field
                      :value="mainRouteMapTable.filters.tripbitIds"
                      @input="v=>mainRouteMapTable.filters.tripbitIds=v"
                      type="text"
                      label="tripbits IDs"
                      clearable
                      dense
                    />
                  </td>
                  <td>
                    <v-autocomplete
                      ref="iataselect"
                      v-model="mainRouteMapTable.filters.iata"
                      :items="iataList"
                      :filter="nonDiacriticsSensitiveFilter"
                      label="iata"
                      clearable
                      multiple
                      dense
                      chips
                      small-chips
                      deletable-chips
                      :menu-props="{
                          closeOnContentClick: true,
                        }"
                    />
                    <v-autocomplete
                      ref="icaoselect"
                      v-model="mainRouteMapTable.filters.icao"
                      :items="icaoList"
                      :filter="nonDiacriticsSensitiveFilter"
                      label="icao"
                      clearable
                      multiple
                      dense
                      chips
                      small-chips
                      deletable-chips
                      :menu-props="{
                          closeOnContentClick: true,
                        }"
                    />
                  </td>
                  <td/>
                </tr>
              </template>

              <template v-slot:item.route_name="{ item }">
                {{ item.is_without_route ? '' : item.route_name }}
              </template>

              <template v-slot:item.pn_ids="{ item }">
                {{ item.pn_ids.join(', ') }}
              </template>
              <template v-slot:item.iata_icao="{ item }">
                <div v-for="(el, index) in item.iata_icao" :key="index">
                  {{ el }}
                </div>
              </template>
              <template v-slot:item.geotags="{ item }">
                <div v-for="(el, index) in item.geotags" :key="index">
                  {{ el }}
                </div>
              </template>
              <template v-slot:item.tb_ids="{ item }">
                {{ item.tb_ids.join(', ') }}
              </template>

            </v-data-table>
          </v-col>
        </v-row>
      </v-tab-item>
      <v-tab-item
        :key="3"
      >
        <v-container>
          <v-row>
            <v-col cols="6">
              <v-card>
                <v-card-title>Bad words</v-card-title>
                <v-card-text>
                  <p>You can use the following patterns:</p>
                  <ul>
                    <li>word* - any ending</li>
                    <li>*word* - substring</li>
                    <li>word+word - phrase</li>
                    <li>word*+word* - phrase with any endings</li>
                    <li>word - case insensitive</li>
                    <li>"word" - case sensitive</li>
                  </ul>
                  <p>Allow only A-Z a-z 0-9 " + and *</p>
                  <v-text-field
                    v-model="newBadWord"
                    append-outer-icon="mdi-label"
                    label="New bad word"
                    @click:append-outer="addBadContentElement('bad_words', 'newBadWord')"
                  ></v-text-field>
                  <v-chip
                    v-for="(word, index) in customer.bad_words"
                    :key="index"
                    close
                    @click:close="deleteBadContentElement('bad_words', index)"
                    class="ma-2"
                    dark
                    small
                  >
                    {{ word }}
                  </v-chip>
                  <v-row class="mt-2">
                    <v-col cols="6">
                      <v-textarea
                        outlined
                        v-model="testBadWordText"
                        label="Test text"
                      ></v-textarea>
                      <v-btn outlined @click="testBadWord">
                        Test text by bad words
                      </v-btn>
                    </v-col>
                    <v-col cols="6">
                      <div
                        v-for="(list, badWord) in resultTestBadWordText"
                        :key="badWord">
                        <span>{{ badWord }}: {{ list.join(', ') }}</span>

                      </div>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="6">
              <v-card>
                <v-card-title>Bad tags</v-card-title>
                <v-card-text>
                  <v-autocomplete
                    v-model="newBadTags"
                    :items="tagsList"
                    :filter="nonDiacriticsSensitiveFilter"
                    item-text="name"
                    item-value="id"
                    label="New bad tag"
                    return-object
                    append-outer-icon="mdi-label"
                    @click:append-outer="addBadContentElement('bad_tags', 'newBadTags')"
                  ></v-autocomplete>
                  <v-chip
                    v-for="(tag, index) in customer.bad_tags"
                    :key="index"
                    close
                    @click:close="deleteBadContentElement('bad_tags', index)"
                    class="ma-2"
                    dark
                    small
                  >
                    {{ tag }}
                  </v-chip>
                </v-card-text>
              </v-card>
              <v-card class="mt-2">
                <v-card-title>Bad POI ids</v-card-title>
                <v-card-text>
                  <v-text-field
                    v-model="newBadPoiId"
                    append-outer-icon="mdi-label"
                    label="New bad id"
                    @click:append-outer="addBadContentElement('bad_poi_ids', 'newBadPoiId')"
                  ></v-text-field>
                  <v-chip
                    v-for="(id, index) in customer.bad_poi_ids"
                    :key="index"
                    close
                    @click:close="deleteBadContentElement('bad_poi_ids', index)"
                    class="ma-2"
                    dark
                    small
                  >
                    {{ id }}
                  </v-chip>
                </v-card-text>
              </v-card>
              <v-card class="mt-2">
                <v-card-title>Hidden Geotags</v-card-title>
                <v-card-text>
                  <EditableList
                      label="geotags"
                      :object="customer"
                      :values="customer.bad_geotags"
                      :item-text-function="badGeotagsText"
                      :list-type-object="$constants.DATA_LISTS.GEOTAG_LIST"
                      description-field="id"
                      @addItem="addHiddenGeotag"
                      @removeItem="removeHiddenGeotag"
                  />
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
      </v-tab-item>
      <v-tab-item
          :key="4"
      >
        <v-toolbar flat>
          <v-btn
              class="mx-2" color="primary"
              dark small
              @click="updateRegions()"
          >
            Get Regions
          </v-btn>
          <confirm-dialog
              :is-open.sync="bulkGrouptagsModel.isOpened"
              width="400"
              @confirm="bulkUpdateGrouptags"
              confirm-button-text="add"
          >
            <template v-slot:activator>
              <v-btn
                  class="mx-2" color="cyan darken-3"
                  dark small
                  @click="openBulkGrouptagsModel"
              >
                Bulk update grouptags
              </v-btn>
            </template>
            <template v-slot:title>
              Update Grouptags
            </template>
            <template v-slot:body>
              <div>Selected {{ geotagsSelected.length }} Geotags</div>
              <v-autocomplete
                  v-model="bulkGrouptagsModel.grouptags"
                  :items="grouptagFilters.grouptagsItems"
                  item-text="name"
                  item-value="id"
                  label="grouptags"
                  return-object
                  clearable
                  multiple
                  dense
                  chips
                  small-chips
                  deletable-chips
                  :menu-props="{ closeOnContentClick: true }"
              />
              <v-switch
                  class="mt-3"
                  dense
                  hide-details
                  v-model="bulkGrouptagsModel.delete"
                  label="delete grouptags"
              />
            </template>
          </confirm-dialog>
        </v-toolbar>
        <v-data-table
            :options.sync="grouptagTable.options"
            :footer-props="{ itemsPerPageOptions: [25, 50, 100, 200 -1] }"
            :headers="grouptagsTableHeaders"
            :items="grouptagTotal"
            :loading="standby"
            :show-select="true"
            v-model="geotagsSelected"
            loading-text="Loading... Please wait"
            :single-expand="true"
        >
          <template v-slot:body.prepend>
            <tr class="filter-row">
              <td>
              </td>
              <td>
                <v-text-field
                    :value="grouptagFilters.id"
                    @input="v=>grouptagFilters.id=v"
                    type="number"
                    label="id"
                    clearable
                    dense
                />
              </td>
              <td>
                <v-text-field
                    :value="grouptagFilters.name"
                    @input="v=>grouptagFilters.name=v"
                    type="text"
                    label="geotag"
                    clearable
                    dense
                />
              </td>
              <td>
                <v-text-field
                    :value="grouptagFilters.region"
                    @input="v=>grouptagFilters.region=v"
                    type="text"
                    label="geotag"
                    clearable
                    dense
                />
              </td>
              <td>
                <v-autocomplete
                    v-model="grouptagFilters.grouptags"
                    :items="grouptagFilters.grouptagsItems"
                    item-text="name"
                    item-value="id"
                    label="grouptags"
                    clearable
                    multiple
                    dense
                    chips
                    small-chips
                    deletable-chips
                    :menu-props="{ closeOnContentClick: true }"
                />
              </td>
            </tr>
          </template>

          <template v-slot:item.grouptags="{ item }">
            <EditableList
                label="grouptags"
                :object="item"
                :values="sortGrouptags(item.grouptags)"
                :select-list="grouptagFilters.grouptagsItems"
                :multiple="true"
                @addItem="addGrouptag"
                @removeItem="removeGrouptag"
            />
          </template>
        </v-data-table>
      </v-tab-item>
    </v-tabs-items>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import EditableList from '@/components/EditableList.vue';
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue';
import XlsxExport from '../components/XlsxExport.vue';
import TripleButton from '../components/TripleButton.vue';
import Chart from '../components/ChartLinear.vue';
import GoogleMapMarkers from '../components/GoogleMapMarkers.vue';
import SimpleList from '../components/SimpleList.vue';
import routeSvg from '../../public/img/icon-route.svg';
import GreyBarWithTitleAndValue from '../components/microcomponents/GreyBarWithTitleAndValue.vue';
import CustomerRouteExpandableTable from '../components/Tables/CustomerRouteExpandableTable.vue';

export default {
  name: 'CustomerShow',
  components: {
    ConfirmDialog,
    EditableList,
    CustomerRouteExpandableTable,
    TripleButton,
    Chart,
    GoogleMapMarkers,
    SimpleList,
    routeSvg,
    GreyBarWithTitleAndValue,
    XlsxExport,
  },
  data: () => ({
    loading: true,
    itemClone: {},
    title: '',
    customer: {},
    customerHeadBars: [
      { title: 'Number of Destinations', value: NaN },
      { title: 'Number of Flights Month', value: NaN },
      { title: 'Number of POI\'s (experiences)', value: NaN },
      { title: 'Number of Reels', value: NaN },
    ],
    customerRoutes: [],
    customerRoutesWithGeotagsWithoutRoutes: [],
    geotagsWithoutRoutes: [],

    geotagDestinationDialog: {
      isOpened: false,
      geotagId: NaN,
      geotagName: '',
      placenamesIds: [],
      routeName: [],
      iataIcao: [],
    },

    table: {
      loading: true,
      options: {
        page: 1,
        itemsPerPage: 50,
        sortBy: ['id'],
        sortDesc: [false],
        mustSort: true,
        multiSort: false,
      },
    },

    routeMapCategories: [],

    mainRouteMapTable: {
      loading: true,
      items: [],
      options: {
        page: 1,
        itemsPerPage: 50,
        sortBy: ['route_flights_amount'],
        sortDesc: [true],
        mustSort: true,
        multiSort: false,
      },
      filters: {
        routeNames: [],
        placenameIds: '',
        geotagNames: [],
        tripbitIds: '',
        iata: [],
        icao: [],
      },
    },
    geotagFilters: {
      id: null,
      name: '',
      pois_count: null,
      pois_with_video_count: null,
      pois_with_images_count: null,
      status: null,
      native_status: null,
      without_attractions_for_native: null,
      is_customer_destination: null,
      weight: null,
      'customers.0.subtype_id': [],
      subtypeItems: [],
    },
    totalGeotags: 0,
    chartsData: {
      overview1: {},
    },
    internalColumnsOptions: [],
    selectedColumns: [],
    showSelectColumns: false,
    customerTabs: [
      'Content',
      'Summary',
      'DB relations',
      'Bad words and Tags',
      'Grouptags',
    ],
    currentCustomerTab: 0,
    geotagCharts: {},
    chartColor: 'rgba(54, 162, 235, 1)',
    chartOptions: {
      legend: {
        display: true,
      },
      scales: {
        yAxes: [{
          ticks: {
            beginAtZero: true,
          },
        }],
      },
      tooltips: {
        callbacks: {
          label(tooltipItem) {
            return tooltipItem.value;
          },
        },
      },
    },
    geotagsTotal: [],
    customerContent: {
      poisTotalAmount: 0,
      videosTotalAmount: 0,
      playlistRating: [],
      providerRating: [],
      hashtagRating: [],
      hashtagGeotagRating: [],
      allVideoDuration: false,
      commonVideoDuration: false,
      reviewsTotalAmount: 0,
      poiHashtagsTotalAmount: 0,
      geotagHashtagsTotalAmount: 0,
    },
    newBadWord: '',
    testBadWordText: '',
    resultTestBadWordText: {},
    newBadTags: '',
    tagsList: [],
    newBadPoiId: '',
    newHiddenGeotagId: '',

    grouptagTable: {
      loading: true,
      options: {
        page: 1,
        itemsPerPage: 50,
        sortBy: ['id'],
        sortDesc: [false],
        mustSort: true,
        multiSort: false,
      },
    },
    grouptagTotal: [],
    grouptagFilters: {
      id: null,
      name: '',
      region: '',
      grouptags: [],
      grouptagsItems: [],
    },
    geotagsSelected: [],
    bulkGrouptagsModel: {
      isOpened: false,
      grouptags: [],
      delete: false,
    },
  }),

  created() {
    this.customer.id = this.$route.params.id;
    this.$requestHelpers
      .get(`api/admin/customer/${this.customer.id}`)
      .then((response) => {
        this.customer = response.data.result;
      });

    this.getChartsGeotagsData();
    this.getRequestedGeotagsPois();
    this.getGrouptagsList();
    this.getSubtypesList();
    this.getCustomerGrouptags();

    this.$requestHelpers
      .get('api/admin/aiResult/filtersList')
      .then((response) => {
        this.tagsList = response.data.result.tags;
      });

    // add default columns list
    this.localReadTableOptions();
    if (this.internalColumnsOptions.length === 0) {
      this.internalColumnsOptions = this.geotagsTableHeaders
        .filter((el) => !Object.hasOwn(el, 'isDefault') || el.isDefault)
        .map((el) => el.value);
    }
    this.selectedColumns = this.internalColumnsOptions;
  },

  methods: {
    ...mapActions({
      setSnack: 'snackbar/setState',
    }),

    exportBBoxes() {
      this.$requestHelpers
        .post('api/admin/customer/exportBBoxes',
          {
            customer_id: this.customer.id,
          },
          {
            responseType: 'blob',
          })
        .then((response) => {
          const fileURL = window.URL.createObjectURL(new Blob([response.data]));
          const fileLink = document.createElement('a');
          const headerval = response.headers['content-disposition'];
          const filename = headerval.match(/filename="(.*)"/);

          fileLink.href = fileURL;
          fileLink.setAttribute('download', filename[1]);
          document.body.appendChild(fileLink);

          fileLink.click();
        });
    },

    save(itemClone, item) {
      return this.$requestHelpers
        .post(`api/admin/customer/updateGeotag/${this.customer.id}`,
          {
            geotag_id: item.id,
            is_destination: itemClone.is_destination,
            weight: itemClone.weight,
            without_attractions_for_native: itemClone.without_attractions_for_native,
            subtype_id: itemClone.subtype_id,
          })
        .then((response) => {
          if (response.data.success) {
            Object.assign(item.customers[0], response.data.result);

            this.setSnack({
              color: 'green',
              text: response.data.message ? response.data.message : 'saved',
            });
          }
        });
    },

    updateWeightByRoutemap(item) {
      return this.$requestHelpers
        .post(`api/admin/customer/updateGeotagWeightByRoutemap/${this.customer.id}`,
          {
            geotag_id: item?.id,
          })
        .then((response) => {
          if (response.data.success) {
            if (item) {
              Object.assign(item.customers[0], response.data.result[0]);
            } else {
              this.getRequestedGeotagsPois();
            }

            this.setSnack({
              color: 'green',
              text: response.data.message ? response.data.message : 'saved',
            });
          }
        });
    },

    saveSelectedColumns() {
      this.internalColumnsOptions = this.selectedColumns;
      this.showSelectColumns = false;
    },

    getChartsGeotagsData() {
      this.$requestHelpers
        .get(`api/admin/customer/customerGeotagsChart/${this.customer.id}`)
        .then((response) => {
          this.geotagCharts = response.data.result;
        });
    },

    getRequestedGeotagsPois() {
      this.$requestHelpers
        .get(`api/admin/customer/customerRequestedGeotags/${this.customer.id}`)
        .then((response) => {
          this.geotagsTotal = response.data.result.geotags_total;
          this.customerContent.poisTotalAmount = response.data.result.pois_amount;
          this.customerContent.videosTotalAmount = response.data.result.videos_amount;
          this.customerContent.playlistRating = response.data.result.top_playlists
            .filter((el) => el.value > 0);
          this.customerContent.providerRating = response.data.result.top_providers
            .filter((el) => el.value > 0);
          this.customerContent.hashtagRating = response.data.result.top_hashtags
            .filter((el) => el.value > 0);
          this.customerContent.hashtagGeotagRating = response.data.result.top_hashtags_geotags
            .filter((el) => el.value > 0);
          this.customerContent.allVideoDuration = response.data.result.all_video_duration;
          this.customerContent.commonVideoDuration = response.data.result.common_video_duration;
          this.routeMapCategories = response.data.result.destinations_rating;

          this.customerContent
            .reviewsTotalAmount = response.data.result.total_provider_reviews_amount;
          this.customerContent
            .poiHashtagsTotalAmount = response.data.result.total_poi_hashtag_amount;
          this.customerContent
            .geotagHashtagsTotalAmount = response.data.result.total_geotag_hashtag_amount;

          this.customerRoutes = response.data.result.routes;
          this.geotagsWithoutRoutes = this.getGeotagsWithoutRoutes();
          this.customerRoutesWithGeotagsWithoutRoutes = this.getRoutesWithGeotagsWithoutRoutes();

          this.customerHeadBars[0].value = [...new Set(this.getRoutesWithGeotagsWithoutRoutes()
            .filter((el) => el.pn_ids.length))].length;
          this.customerHeadBars[1].value = this.customerRoutes
            .reduce((accum, el) => accum + el.route_flights_amount, 0);
          this.customerHeadBars[2].value = this.customerContent.poisTotalAmount;
          this.customerHeadBars[3].value = this.customerContent.videosTotalAmount;
        });
    },

    getGrouptagsList() {
      this.$requestHelpers
        .get('api/admin/grouptags/grouptagsList')
        .then((response) => {
          this.grouptagFilters.grouptagsItems = response.data.result;
        });
    },

    getSubtypesList() {
      this.$requestHelpers
        .get('api/admin/subtypes/subtypeList')
        .then((response) => {
          this.geotagFilters.subtypeItems = response.data.result;
        });
    },

    getCustomerGrouptags() {
      this.$requestHelpers
        .get(`api/admin/grouptags/customerGrouptags/${this.customer.id}`)
        .then((response) => {
          this.grouptagTotal = response.data.result;
        });
    },

    extractGeotagsFromCustomerRoutes() {
      this.$requestHelpers
        .get(`api/admin/customer/extractGeotagsFromCustomerRoutes/${this.customer.id}`)
        .then((response) => {
          if (response.data.success) {
            this.getChartsGeotagsData();
            this.getRequestedGeotagsPois();
          }
        });
    },

    openGeotagDestinationDialog(geotagId, geotagName) {
      this.resetGeotagDestinationDialog();
      const destinations = this.getGeotagRoutemapDestinations(geotagId);

      if (destinations.length) {
        Object.assign(
          this.geotagDestinationDialog,
          {
            geotagId,
            geotagName,
            placenamesIds: [...new Set(destinations.map((el) => el.pn_ids).flat(2))],
            routeName: destinations.map((el) => el.route_name),
            iataIcao: [...new Set(destinations.map((el) => el.iata_icao).flat(2))],
          },
        );
        this.geotagDestinationDialog.isOpened = true;
      }
    },

    closeGeotagDestinationDialog() {
      this.resetGeotagDestinationDialog();
      this.geotagDestinationDialog.isOpened = false;
    },

    resetGeotagDestinationDialog() {
      Object.assign(
        this.geotagDestinationDialog,
        {
          geotagId: NaN,
          geotagName: '',
          placenamesIds: [],
          routeName: [],
          iataIcao: [],
        },
      );
    },

    getGeotagRoutemapDestinations(geotagId) {
      return this.customerRoutes.filter(
        (el) => el.tb_ids.includes(geotagId),
      );
    },

    getRoutesWithGeotagsWithoutRoutes() {
      return [...this.customerRoutes, ...this.geotagsWithoutRoutes];
    },

    getGeotagsWithoutRoutes() {
      const geotagsWithoutRoutes = [];
      for (let i = 0; i < this.geotagsTotal.length; i += 1) {
        if (this.customerRoutes.find(
          (el) => el.tb_ids.includes(this.geotagsTotal[i].id),
        ) === undefined) {
          geotagsWithoutRoutes.push({
            id: this.customerRoutes.length + geotagsWithoutRoutes.length,
            route_name: this.geotagsTotal[i].placenames_name,
            pn_ids: this.geotagsTotal[i].placenames_id ? [this.geotagsTotal[i].placenames_id] : [],
            route_flights_amount: 0,
            rating_list: ['no_flights'],
            iata_icao: [],
            geotags: [this.geotagsTotal[i].name],
            tb_ids: [this.geotagsTotal[i].id],
            pois_amount: this.geotagsTotal[i].pois_enabled_count,
            reels_amount: this.geotagsTotal[i].videodata_count,
            is_selected: false,
            is_live: [this.geotagsTotal[i].status === 2],
            is_without_route: true,
          });
        }
      }

      return geotagsWithoutRoutes;
    },

    getExportSummaryWorkbook() {
      let filteredItems = [];

      if (this.$refs.summaryroute) {
        const items = this.$refs.summaryroute.$children[0].filteredItems;
        const { sortBy } = this.$refs.summaryroute.$children[0].options;
        const { sortDesc } = this.$refs.summaryroute.$children[0].options;
        filteredItems = this.$refs.summaryroute.customSortWithHeaders(items, sortBy, sortDesc);
      } else {
        filteredItems = this.customerRoutesWithGeotagsWithoutRoutes;
      }

      const filename = `${this.customer.name}_db_relations_${Date.now()}.xlsx`;

      return filteredItems.length === 0
        ? {
          filename,
          sheets: [
            {
              name: 'DB relations',
              data: [{}],
            },
          ],
        }
        : {
          filename,
          sheets: [
            {
              name: 'DB relations',
              data: filteredItems.map((el) => ({
                route_map_destination: el.is_without_route ? '' : el.route_name,
                placenames_ids: el.pn_ids.join(', '),
                geotags: el.geotags.join(', '),
                tripbits_ids: el.tb_ids.join(', '),
                iata: el.iata_icao
                  .map((el2) => el2.split('/').filter((el3) => el3.length === 3)).join(', '),
                icao: el.iata_icao
                  .map((el2) => el2.split('/').filter((el3) => el3.length === 4)).join(', '),
                avg_month_flights: el.route_flights_amount,
              })),
            },
          ],
        };
    },

    addBadContentElement(column, variableName) {
      const value = this[variableName];
      if (value) {
        value.split(',').forEach((item) => {
          const itemValue = item.trim();
          if (itemValue) {
            if (this.customer[column]) {
              this.customer[column].push(itemValue);
              this.customer[column] = this.customer[column].filter((e, i, j) => i === j.indexOf(e));
            } else {
              this.customer[column] = [itemValue];
            }
          }
        });

        this[variableName] = '';
        this.updateCustomer({ [column]: this.customer[column] });
      }
    },

    deleteBadContentElement(column, index) {
      this.customer[column].splice(index, 1);
      this.updateCustomer({ [column]: this.customer[column] });
    },

    badGeotagsText(geotag) {
      return `${geotag.name} ${geotag.id}`;
    },

    addHiddenGeotag(item, geotag) {
      if (!geotag) return;

      this.newHiddenGeotagId = geotag.id.toString();
      this.addBadContentElement('bad_geotags_ids', 'newHiddenGeotagId');
    },

    removeHiddenGeotag(item, geotag) {
      const index = this.customer.bad_geotags_ids.indexOf(geotag.id.toString());
      if (index === -1) return;

      this.deleteBadContentElement('bad_geotags_ids', index);
    },

    updateCustomer(data) {
      this.$requestHelpers
        .patch(`api/admin/customer/${this.customer.id}`, data)
        .then((response) => {
          if (response.data.success) {
            Object.assign(this.customer, response.data.result);

            this.setSnack({
              color: 'green',
              text: 'saved',
            });
          }
        });
    },

    testBadWord() {
      this.$requestHelpers
        .post('api/admin/customer/testBadWords', {
          badWords: this.customer.bad_words,
          testBadWordText: this.testBadWordText,
        })
        .then((response) => {
          if (response.data.success) {
            this.resultTestBadWordText = response.data.result;

            this.setSnack({
              color: 'green',
              text: 'done',
            });
          }
        });
    },

    updateRegions() {
      this.$requestHelpers
        .get('api/admin/clear/cache')
        .then(() => {
          this.getCustomerGrouptags();
        });
    },

    addGrouptag(item, grouptags) {
      if (!grouptags) return;

      const grouptagsIds = grouptags.map((el) => el.id);

      this.updateCustomerGrouptag('add_grouptag', grouptagsIds, [item.id]);
    },

    removeGrouptag(item, grouptag) {
      this.updateCustomerGrouptag('remove_grouptag', [grouptag.id], [item.id]);
    },

    // update
    updateCustomerGrouptag(field, grouptagsIds, geotagsIds) {
      return this.$requestHelpers
        .post('api/admin/grouptags/updateCustomersGrouptags', {
          customer_id: this.customer.id,
          geotags: geotagsIds,
          grouptags: grouptagsIds,
          action: field,
        })
        .then((response) => {
          if (response.data.success) {
            this.setSnack({
              color: 'green',
              text: response.data.message ? response.data.message : 'saved',
            });
          }

          this.getCustomerGrouptags();
        }).catch((response) => {
          this.setSnack({
            color: 'red',
            text: response.data.message ? response.data.message : 'Error',
          });
        });
    },

    openBulkGrouptagsModel() {
      if (this.geotagsSelected.length === 0) {
        this.setSnack({
          text: 'Empty selected geotags',
          color: 'red',
          timeout: 5000,
        });
        return;
      }

      this.bulkGrouptagsModel.isOpened = true;
    },

    bulkUpdateGrouptags() {
      const geotagsIds = this.geotagsSelected.map((el) => el.id);
      const grouptagsIds = this.bulkGrouptagsModel.grouptags.map((el) => el.id);

      if (this.bulkGrouptagsModel.delete) {
        this.updateCustomerGrouptag('remove_grouptag', grouptagsIds, geotagsIds);
      } else {
        this.updateCustomerGrouptag('add_grouptag', grouptagsIds, geotagsIds);
      }

      this.bulkGrouptagsModel.isOpened = false;
      this.bulkGrouptagsModel.grouptags = [];
      this.bulkGrouptagsModel.delete = false;
    },

    sortGrouptags(grouptags) {
      const list = Array.isArray(grouptags) ? grouptags : [];
      return [...list].sort((a, b) => a.id - b.id);
    },

    getSubtypeName(subtypeId) {
      const subtype = this.geotagFilters.subtypeItems.find((item) => item.id === subtypeId);

      return subtype ? subtype.name : 'empty';
    },

    // get/set selected columns
    localReadTableOptions() {
      let storageData = [];
      if (localStorage.table_options_customer_geotag_table) {
        storageData = localStorage.table_options_customer_geotag_table.split(',');
      }
      this.internalColumnsOptions = storageData;
    },
    localSaveTableOptions() {
      localStorage.table_options_customer_geotag_table = this.internalColumnsOptions.join(',');
    },
  },

  watch: {
    async newBadWord(newValue) {
      const re = /[^A-Za-z0-9"+*]/gi;
      const filteredValue = newValue.replace(re, '');
      if (filteredValue !== newValue) {
        this.newBadWord = '';
        await this.$nextTick(); // hook for update value in input if set wrong chars
        this.newBadWord = filteredValue;
      }
    },
    internalColumnsOptions() {
      this.localSaveTableOptions();
    },
  },

  computed: {
    ...mapGetters({
      standby: 'overlay/show',
    }),

    routeNamesList() {
      const diversedNamesList = this.customerRoutes.map(
        (item) => item.route_name.split('/'),
      ).flat().sort();

      diversedNamesList.unshift('<empty>');

      return [...new Set(diversedNamesList)];
    },

    geotagNamesList() {
      const diversedNamesList = this.geotagsTotal.map(
        (item) => item.name,
      ).flat().sort();

      diversedNamesList.unshift('<empty>');

      return [...new Set(diversedNamesList)];
    },

    iataList() {
      if (!this.customerRoutes) return [];

      return [...new Set(
        this.customerRoutes.map(
          (el) => el.iata_icao.map(
            (iataIcao) => iataIcao.split('/').filter((code) => code.length === 3),
          ),
        ).flat(2).sort(),
      )];
    },

    icaoList() {
      if (!this.customerRoutes) return [];

      return [...new Set(
        this.customerRoutes.map(
          (el) => el.iata_icao.map(
            (iataIcao) => iataIcao.split('/').filter((code) => code.length === 4),
          ),
        ).flat(2).sort(),
      )];
    },

    mainRouteMapTableHeaders() {
      return [
        {
          text: 'Route Map Destination',
          value: 'route_name',
          filter: (value, search, item) => {
            if (this.mainRouteMapTable.filters.routeNames.length === 0) {
              return true;
            }

            const isMatchedEmpty = this.mainRouteMapTable.filters.routeNames.includes('<empty>')
            && item.is_without_route;

            return isMatchedEmpty || this.mainRouteMapTable.filters.routeNames.some(
              (el) => (`${value}`).split('/').includes(el),
            );
          },
        },
        {
          text: 'Placenames IDs',
          value: 'pn_ids',
          filter: (value) => {
            if (!this.mainRouteMapTable.filters.placenameIds) return true;
            const list = this.mainRouteMapTable.filters.placenameIds
              .replaceAll(',', ' ').split(' ').map((item) => parseInt(item, 10));

            return value.some((el) => list.includes(el));
          },
        },
        {
          text: 'Geotags',
          value: 'geotags',
          filter: (value) => {
            if (this.mainRouteMapTable.filters.geotagNames.length === 0) {
              return true;
            }

            const isMatchedEmpty = this.mainRouteMapTable.filters.geotagNames.includes('<empty>')
              && value.length === 0;

            return isMatchedEmpty || this.mainRouteMapTable.filters.geotagNames.some(
              (el) => value.includes(el),
            );
          },
        },
        {
          text: 'Tripbits ID',
          value: 'tb_ids',
          filter: (value) => {
            if (!this.mainRouteMapTable.filters.tripbitIds) return true;
            const list = this.mainRouteMapTable.filters.tripbitIds
              .replaceAll(',', ' ').split(' ').map((item) => parseInt(item, 10));

            return value.some((el) => list.includes(el));
          },
        },
        {
          text: 'Destinations',
          value: 'iata_icao',
          filter: (value) => {
            const isIataFilterApplied = this.mainRouteMapTable.filters.iata.length !== 0;
            const isIcaoFilterApplied = this.mainRouteMapTable.filters.icao.length !== 0;

            if (!isIataFilterApplied && !isIcaoFilterApplied) {
              return true;
            }

            const iata = [
              ...new Set(
                value.map(
                  (el) => el.split('/').filter(
                    (code) => code.length === 3,
                  ),
                ).flat(),
              ),
            ];
            const icao = [
              ...new Set(
                value.map(
                  (el) => el.split('/').filter(
                    (code) => code.length === 4,
                  ),
                ).flat(),
              ),
            ];

            const isIataMatch = !isIataFilterApplied
              ? true
              : this.mainRouteMapTable.filters.iata.some((el) => iata.includes(el));

            const isIcaoMatch = !isIcaoFilterApplied
              ? true
              : icao.length !== 0
              && this.mainRouteMapTable.filters.icao.some((el) => icao.includes(el));

            return isIataMatch && isIcaoMatch;
          },
        },
        {
          text: 'Route flights (month avg)',
          value: 'route_flights_amount',
        },
      ];
    },

    computedGeotagsHeaders() {
      return this.geotagsTableHeaders.filter((el) => this.internalColumnsOptions.includes(el.value));
    },

    geotagsTableHeaders() {
      return [
        {
          text: 'id',
          value: 'id',
          width: '60px',
          isRequired: true,
          filter: (value) => {
            if (!this.geotagFilters.id) return true;

            return value === parseInt(this.geotagFilters.id, 10);
          },
        },
        {
          text: 'geotag',
          value: 'name',
          filter: (value) => {
            if (!this.geotagFilters.name) return true;

            return (`${value}`).toLowerCase().includes(this.geotagFilters.name.toLocaleLowerCase());
          },
        },
        {
          text: 'pois amount',
          value: 'pois_count',
          filter: (value) => {
            if (!this.geotagFilters.pois_count) return true;

            return value === parseInt(this.geotagFilters.pois_count, 10);
          },
        },
        {
          text: 'pois with videos',
          value: 'pois_with_video_count',
          filter: (value) => {
            if (!this.geotagFilters.pois_with_video_count) return true;

            return value === parseInt(this.geotagFilters.pois_with_video_count, 10);
          },
        },
        {
          text: 'pois with images',
          value: 'pois_with_images_count',
          filter: (value) => {
            if (!this.geotagFilters.pois_with_images_count) return true;

            return value === parseInt(this.geotagFilters.pois_with_images_count, 10);
          },
        },
        {
          text: 'status',
          value: 'status',
          filterOptions: { type: 'select', items: ['off', 'prev', 'prev only', 'live'] },
          filter: (value) => {
            if (!this.geotagFilters.status) return true;

            let filterValue;
            switch (this.geotagFilters.status) {
              case 'off':
                filterValue = [0];
                break;
              case 'prev only':
                filterValue = [1];
                break;
              case 'prev':
                filterValue = [1, 2];
                break;
              case 'live':
                filterValue = [2];
                break;
              default:
                filterValue = [];
            }

            return filterValue.includes(value);
          },
        },
        {
          text: 'native status',
          value: 'native_status',
          filterOptions: { type: 'select', items: ['off', 'on'] },
          filter: (value, search, item) => {
            if (!this.geotagFilters.native_status) return true;

            switch (this.geotagFilters.native_status) {
              case 'on':
                return item.native_status === 1;
              case 'off':
                return item.native_status === 0;
              default:
                return true;
            }
          },
        },
        {
          text: 'without attractions for native',
          value: 'without_attractions_for_native',
          filterOptions: { type: 'select', items: ['off', 'on'] },
          filter: (value, search, item) => {
            if (!this.geotagFilters.without_attractions_for_native) return true;

            switch (this.geotagFilters.without_attractions_for_native) {
              case 'on':
                return item.customers[0].without_attractions_for_native === 1;
              case 'off':
                return item.customers[0].without_attractions_for_native === 0;
              default:
                return true;
            }
          },
        },
        {
          text: 'is_destination',
          value: 'is_destination',
          filterOptions: { type: 'select', items: ['off', 'on'] },
          filter: (value, search, item) => {
            if (!this.geotagFilters.is_destination) return true;

            switch (this.geotagFilters.is_destination) {
              case 'on':
                return item.customers[0].is_destination === 1;
              case 'off':
                return item.customers[0].is_destination === 0;
              default:
                return true;
            }
          },
        },
        {
          text: 'weight',
          value: 'customers.0.weight',
          filter: (value) => {
            if (!this.geotagFilters['customers.0.weight']) return true;

            return (`${value}`).toLowerCase()
              .includes(this.geotagFilters['customers.0.weight'].toLocaleLowerCase());
          },
        },
        {
          text: 'subtype',
          value: 'customers.0.subtype_id',
          filterOptions: { type: 'multipleSelect', items: this.geotagFilters.subtypeItems },
          filter: (value) => {
            if (!this.geotagFilters['customers.0.subtype_id'].length) return true;

            return this.geotagFilters['customers.0.subtype_id'].includes(value);
          },
        },
      ];
    },

    isGeotagRoutemapDestination() {
      return (geotagId) => this.customerRoutes.find(
        (el) => el.tb_ids.includes(geotagId),
      ) !== undefined;
    },

    geotagsOnMap() {
      const selectedRoutes = this.customerRoutesWithGeotagsWithoutRoutes
        .filter((el) => el.is_selected);
      const geotagIdsInSelectedRoutes = [...new Set(selectedRoutes.map((el) => el.tb_ids).flat())];

      return this.geotagsTotal
        .filter((el) => geotagIdsInSelectedRoutes.includes(el.id));
    },

    grouptagsTableHeaders() {
      return [
        {
          text: 'id',
          value: 'id',
          width: '60px',
          filter: (value) => {
            if (!this.grouptagFilters.id) return true;

            return value === parseInt(this.grouptagFilters.id, 10);
          },
        },
        {
          text: 'geotag',
          value: 'name',
          filter: (value) => {
            if (!this.grouptagFilters.name) return true;

            return (`${value}`).toLowerCase()
              .includes(this.grouptagFilters.name.toLocaleLowerCase());
          },
        },
        {
          text: 'region',
          value: 'region.regionName',
          filter: (value) => {
            if (!this.grouptagFilters.region) return true;

            return (`${value}`).toLowerCase()
              .includes(this.grouptagFilters.region.toLocaleLowerCase());
          },
        },
        {
          text: 'grouptags',
          value: 'grouptags',
          filter: (value) => {
            if (!this.grouptagFilters.grouptags.length) return true;

            const valueArray = Array.isArray(value) ? value : [value];
            const valueIds = valueArray.map((el) => el.id);

            return this.grouptagFilters.grouptags.some((tagId) => valueIds.includes(tagId));
          },
        },
      ];
    },
  },
};
</script>
